import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SimpleIndexedDBService {
  private dbName = 'LausdBtbDB';
  private version = 1;
  private storeName = 'keyValueStore';
  private db: IDBDatabase | null = null;
  private encryptionKey: CryptoKey | null = null;
  private encryptionKeyPromise: Promise<void> | null = null;

  constructor() {
    this.initDB();
    this.encryptionKeyPromise = this.initEncryptionKey();
  }

  private async initDB(): Promise<void> {
    try {
      this.db = await this.openDB();
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error);
    }
  }

  private async ensureEncryptionKey(): Promise<void> {
    if (this.encryptionKeyPromise) {
      await this.encryptionKeyPromise;
    }
    
    if (!this.encryptionKey) {
      throw new Error('Encryption key not available');
    }
  }

  private async initEncryptionKey(): Promise<void> {
    try {
      // Hardcoded encryption key (base64 encoded raw key)
      const hardcodedKey = 'f8b1c4b7-5a1e-4c7b-9e2d-3a5f7e9b0d1c'; // 32 bytes for AES-256
      
      // Convert the hardcoded key to ArrayBuffer
      const keyBuffer = new TextEncoder().encode(hardcodedKey.padEnd(32, '0').substring(0, 32));
      
      // Import the key
      this.encryptionKey = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
    } catch (error) {
      console.error('Failed to initialize encryption key:', error);
    }
  }

  private async encryptData(data: any): Promise<string> {
    // Wait for encryption key to be ready
    await this.ensureEncryptionKey();

    try {
      const jsonString = JSON.stringify(data);
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(jsonString);
      
      // Generate a random IV for each encryption
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        this.encryptionKey,
        dataBuffer
      );
      
      // Combine IV and encrypted data
      const combinedBuffer = new Uint8Array(iv.length + encryptedBuffer.byteLength);
      combinedBuffer.set(iv);
      combinedBuffer.set(new Uint8Array(encryptedBuffer), iv.length);
      
      // Convert to base64 for storage
      return btoa(String.fromCharCode.apply(null, Array.from(combinedBuffer)));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw error;
    }
  }

  private async decryptData(encryptedData: string): Promise<any> {
    // Wait for encryption key to be ready
    await this.ensureEncryptionKey();

    try {
      // Convert from base64
      const combinedBuffer = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );
      
      // Extract IV and encrypted data
      const iv = combinedBuffer.slice(0, 12);
      const encryptedBuffer = combinedBuffer.slice(12);
      
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        this.encryptionKey,
        encryptedBuffer
      );
      
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(decryptedBuffer);
      
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw error;
    }
  }

  async setItem(key: string, value: any): Promise<void> {
    try {
      // Encrypt the value before storing
      const encryptedValue = await this.encryptData(value);
      
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.put({ key, value: encryptedValue });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB setItem error:', error);
      // Fallback to localStorage with encryption
      try {
        const encryptedValue = await this.encryptData(value);
        localStorage.setItem(key, encryptedValue);
      } catch (fallbackError) {
        console.error('localStorage fallback failed:', fallbackError);
        throw error;
      }
    }
  }

  async getItem(key: string): Promise<any> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise(async (resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = async () => {
          const result = request.result;
          if (result && result.value) {
            try {
              // Decrypt the value before returning
              const decryptedValue = await this.decryptData(result.value);
              resolve(decryptedValue);
            } catch (decryptError) {
              console.error('Decryption error:', decryptError);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB getItem error:', error);
      // Fallback to localStorage with decryption
      try {
        const encryptedItem = localStorage.getItem(key);
        if (encryptedItem) {
          return await this.decryptData(encryptedItem);
        }
        return null;
      } catch (fallbackError) {
        console.error('localStorage fallback failed:', fallbackError);
        return null;
      }
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB removeItem error:', error);
      // Fallback to localStorage
      try {
        localStorage.removeItem(key);
      } catch (fallbackError) {
        console.error('localStorage fallback failed:', fallbackError);
        throw error;
      }
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB clear error:', error);
      // Fallback to localStorage
      try {
        localStorage.clear();
      } catch (fallbackError) {
        console.error('localStorage fallback failed:', fallbackError);
        throw error;
      }
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.getAllKeys();
        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB getAllKeys error:', error);
      return [];
    }
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          console.log('Created IndexedDB object store:', this.storeName);
        }
      };
    });
  }

  // Check if IndexedDB is available
  isIndexedDBAvailable(): boolean {
    return 'indexedDB' in window && indexedDB !== null;
  }

  // Check if Web Crypto API is available
  isCryptoAvailable(): boolean {
    return 'crypto' in window && 'subtle' in window.crypto;
  }

  // Method to change the hardcoded encryption key (use with caution - will make existing data unreadable)
  async changeEncryptionKey(newKey: string): Promise<void> {
    try {
      // Convert the new key to ArrayBuffer
      const keyBuffer = new TextEncoder().encode(newKey.padEnd(32, '0').substring(0, 32));
      
      this.encryptionKey = await window.crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
      
      // Update the promise to resolve immediately since key is ready
      this.encryptionKeyPromise = Promise.resolve();
      
      console.log('Encryption key changed successfully');
    } catch (error) {
      console.error('Failed to change encryption key:', error);
      throw error;
    }
  }

  // Get database info for debugging
  async getDBInfo(): Promise<any> {
    try {
      const keys = await this.getAllKeys();
      return {
        dbName: this.dbName,
        version: this.version,
        storeName: this.storeName,
        keysCount: keys.length,
        keys: keys,
        isAvailable: this.isIndexedDBAvailable(),
        isCryptoAvailable: this.isCryptoAvailable(),
        hasEncryptionKey: !!this.encryptionKey
      };
    } catch (error) {
      return {
        error: error.message,
        isAvailable: this.isIndexedDBAvailable(),
        isCryptoAvailable: this.isCryptoAvailable(),
        hasEncryptionKey: !!this.encryptionKey
      };
    }
  }
}